// Small info icon that opens a hint card: title + explanation.
// Rendered in a portal so panels never clip it, positioned next to the icon and flipped near screen edges.
// Opens on hover (short delay), keyboard focus or tap; Esc / click elsewhere closes it.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../concession.css';

const OPEN_DELAY = 150;
const CARD_WIDTH = 260;
const MARGIN = 10;

const Hint = ({ title, text, label = 'More info' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const timer = useRef(null);
  const id = `cx-hint-${React.useId().replace(/:/g, '')}`;

  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), OPEN_DELAY);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setOpen(false);
  };

  // Place below the icon by default; flip above / clamp horizontally to stay on screen
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const card = cardRef.current;
    if (!trigger || !card) return;
    const r = trigger.getBoundingClientRect();
    const h = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left + r.width / 2 - CARD_WIDTH / 2;
    left = Math.max(MARGIN, Math.min(left, vw - CARD_WIDTH - MARGIN));
    const below = r.bottom + 8;
    const above = r.top - h - 8;
    const placeAbove = below + h > vh - MARGIN && above > MARGIN;
    setPos({
      top: placeAbove ? above : below,
      left,
      side: placeAbove ? 'above' : 'below',
      arrow: r.left + r.width / 2 - left,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && hide();
    const onDown = (e) => {
      if (!triggerRef.current?.contains(e.target) && !cardRef.current?.contains(e.target)) hide();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`cx-hint-trigger${open ? ' open' : ''}`}
        aria-label={title ? `About ${typeof title === 'string' ? title : 'this'}` : label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          clearTimeout(timer.current);
          setOpen((o) => !o);
        }}
      >
        <i className="bi bi-info-circle" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            ref={cardRef}
            id={id}
            role="tooltip"
            className={`cx-hint-card${pos ? ` ready ${pos.side}` : ''}`}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: CARD_WIDTH, '--cx-hint-arrow': `${pos?.arrow ?? CARD_WIDTH / 2}px` }}
          >
            {title && <div className="cx-hint-title">{title}</div>}
            <div className="cx-hint-text">{text}</div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Hint;
